import { useState } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, { IBrandingApi } from '@services/BrandingsService';
import EventsService from '@services/EventsService';

import { buildBrandingSwitchEntities } from './brandingEntities';
import { sendBrandingEntitiesUpdatedToUnreal } from './brandingSpotsUnreal';

interface IUseBrandingSwitchOptions {
  /** Autosave indicator, where the caller has one. */
  onStatus?: (status: 'saving' | 'saved' | 'error') => void;
  /** Runs once the switch has fully landed, e.g. to close the picker. */
  onSwitched?: () => void;
}

/**
 * Makes a configuration live while the editor is open.
 *
 * Activation on its own is only half the job in editor mode: the backend nudges
 * UE to re-read its spots, but the per-entity property overrides are pushed by
 * the hub, so UE would keep applying the previous configuration's overrides on
 * top of the new one. Every editor-side entry point has to run this same
 * sequence, which is why it lives here rather than in one of them.
 *
 * @param options - Optional caller hooks; see `IUseBrandingSwitchOptions`.
 * @returns `switchBranding` to perform the switch and `isSwitching` while one
 * is in flight.
 */
export const useBrandingSwitch = ({
  onStatus,
  onSwitched,
}: IUseBrandingSwitchOptions = {}) => {
  const {
    currentRoom,
    setCurrentRoom,
    currentBrandingId,
    setCurrentBrandingId,
    setBrandingSpots,
    setRoomSpots,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const [isSwitching, setIsSwitching] = useState(false);

  const roomId = currentRoom?.roomId;

  /**
   * @param brandingId - Configuration to make live.
   * @param brandings - Configurations of the current map, read for the entity
   * overrides of the one being left and the one being taken.
   */
  const switchBranding = async (
    brandingId: string,
    brandings: IBrandingApi[],
  ) => {
    if (!brandingId || !roomId || isSwitching) {
      return;
    }
    setIsSwitching(true);
    onStatus?.('saving');
    // Read before the state update below, since the union payload needs the
    // configuration being left.
    const previousBrandingId = currentBrandingId;
    try {
      setCurrentBrandingId(brandingId);
      sendMessageToUnreal({ action: 'CLEAR_SPOTS' });
      sendMessageToUnreal({ action: 'ENTER_TO_CUSTOMIZATION_MODE' });
      await BrandingsService.activateBranding(brandingId, roomId);
      setCurrentRoom((prev) =>
        prev && prev.roomId === roomId
          ? { ...prev, activeBrandingId: brandingId }
          : prev,
      );
      sendMessageToUnreal({
        action: 'BRANDING_ACTIVATED',
        brandingId,
        roomId,
      });

      // Per-entity replace cannot unset an entity absent from the payload, so
      // the switch sends the union of both configurations: the new values, plus
      // an empty map for every entity the new one drops, which restores what the
      // level authored. One message, one rebuild per object — a CLEAR_BRANDING
      // first would rebuild everything twice.
      sendBrandingEntitiesUpdatedToUnreal(
        sendMessageToUnreal,
        buildBrandingSwitchEntities(
          brandings.find(
            (branding) => branding.brandingId === previousBrandingId,
          )?.brandingEntities,
          brandings.find((branding) => branding.brandingId === brandingId)
            ?.brandingEntities,
        ),
      );
      const [brandingSpotsRes, roomSpotsRes] = await Promise.all([
        BrandingsService.getBrandingSpots(brandingId, { limit: 200 }),
        EventsService.getRoomSpots(roomId),
      ]);
      setBrandingSpots(brandingSpotsRes.spots ?? []);
      setRoomSpots(roomSpotsRes);
      onStatus?.('saved');
      onSwitched?.();
    } catch (selectError) {
      console.error(
        `Failed to select+activate branding: ${
          (selectError as Error)?.message
        }`,
        { brandingId, previousBrandingId, roomId },
      );

      // The local switch happens before the request, so a failed activation leaves the client
      // claiming a configuration the room never adopted — and the map-editor bridge has already
      // republished that configuration's layout to everyone, since a publish is authoritative and
      // does not wait on this call. Putting the id back is what undoes both: the bridge re-reads the
      // configuration that is still live and republishes it, which is a switch by its own reckoning
      // and so is sent even when that layout is empty.
      //
      // Reverting to a null id is correct rather than a gap: `useActiveBrandingId` then falls through
      // to `currentRoom.activeBrandingId`, which still names the old configuration because
      // setCurrentRoom below only runs once the activation has landed.
      setCurrentBrandingId(previousBrandingId);

      // Spots were cleared in Unreal on the way in, and nothing has restored them: the success path
      // does that with the entity payload and the spot re-fetch further down, neither of which ran.
      // Naming the configuration that is still active is the standing way to ask UE to re-fetch its
      // authoritative state, so this restores what CLEAR_SPOTS took away.
      if (previousBrandingId) {
        sendMessageToUnreal({
          action: 'BRANDING_ACTIVATED',
          brandingId: previousBrandingId,
          roomId,
        });
      }

      onStatus?.('error');
    } finally {
      setIsSwitching(false);
    }
  };

  return { switchBranding, isSwitching };
};
